import { Button, Input } from '@/lib';
import { Workspace } from '@/models/workspace';
import { useInviteWorkspaceMutation } from '@/services/endpoints/workspaces';
import { Form, Formik, FormikValues } from 'formik';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

type Role = 'ADMIN' | 'READONLY';

type RoleItem = {
  id: Role;
  label: string;
};

interface NewWorkspaceInviteRowProps {
  onComplete: (saved: boolean) => void;
  workspace: Workspace;
}

export const NewWorkspaceInviteRow: FC<NewWorkspaceInviteRowProps> = ({ onComplete, workspace }) => {
  const { t } = useTranslation('workspaces');
  const { t: tOthers } = useTranslation();

  const [inviteWorkspaceUser, { data, isLoading, isError }] = useInviteWorkspaceMutation();

  useEffect(() => {
    if (data && !isLoading && !isError) {
      onComplete(false);
    }
  }, [data, isLoading, isError, onComplete]);

  const roleItems: RoleItem[] = [
    {
      id: 'ADMIN',
      label: t('users.administrator'),
    },
    {
      id: 'READONLY',
      label: t('users.readOnly'),
    },
  ];

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(tOthers('settings:company.errors.emailValid') as string)
      .required(tOthers('settings:company.errors.emailRequired') as string),
    role: Yup.string().required(tOthers('settings:company.errors.roleRequired') as string),
  });

  const handleSaveButtonClicked = async ({ email, role }: FormikValues) => {
    inviteWorkspaceUser({
      workspaceId: workspace.id,
      collaborator: {
        email,
        role,
      },
    });
  };

  const handleCancelButtonClicked = () => {
    onComplete(false);
  };

  return (
    <div className='rounded-lg border border-primary-border bg-primary-background p-6 shadow-xs'>
      <Formik initialValues={{ email: '', role: roleItems[0].id }} validationSchema={validationSchema} onSubmit={handleSaveButtonClicked}>
        {({ values, handleChange, errors, touched }) => (
          <Form>
            <div className='mb-6 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_auto]'>
              <div className='flex items-center gap-2'>
                <label htmlFor='email' className='min-w-[45px] text-xs font-medium text-primary-900'>
                  {t('users.email')}
                </label>
                <Input
                  id='email'
                  name='email'
                  type='email'
                  placeholder={t('emailPlaceholder')}
                  value={values.email}
                  onChange={(e) => handleChange('email')(e.target.value)}
                  disabled={isLoading}
                  errorMsg={touched.email && errors.email ? errors.email : undefined}
                  className='flex-1 text-sm'
                />
              </div>

              <div className='flex items-center gap-2'>
                <label htmlFor='role' className='min-w-[35px] text-xs font-medium text-primary-900'>
                  {t('users.role')}
                </label>
                <select
                  id='role'
                  name='role'
                  value={values.role}
                  onChange={(e) => handleChange('role')(e.target.value)}
                  disabled={isLoading}
                  className='flex-1 rounded-lg border border-primary-border bg-primary-background px-2 py-1.5 text-sm text-primary-900 shadow-xs focus:border-brand-600 focus:ring-2 focus:ring-brand-600 disabled:cursor-not-allowed disabled:bg-disabled-background disabled:text-disabled-subtle'
                >
                  {roleItems.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex justify-end gap-2 lg:justify-start'>
                <Button intent='secondary' onClick={handleCancelButtonClicked} disabled={isLoading} size='sm'>
                  {t('users.cancel')}
                </Button>
                <Button intent='primary' type='submit' disabled={isLoading} isLoading={isLoading} size='sm'>
                  {t('users.inviteUser')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
